import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { worldBounds } from './babylonScene';

export { isBedType } from '../itemTypes';

// The client finds a bed's special meshes by substring (meshesSchema isMattressMesh /
// isBlanketMesh); the mattress doubles as a container (pillows go on the bed) and an
// invisible container plane rides on top of the blanket so items rest on the bedding.
export const MATTRESS_MESH_NAME = '$$container_mattress';
export const BLANKET_MESH_NAME = '$$noncollidable_blanket';
export const BLANKET_CONTAINER_MESH_NAME = '$$container_blanket_invisible';

export const isMattressMeshName = (name) => typeof name === 'string' && name.includes('mattress');
export const isBlanketMeshName = (name) => typeof name === 'string' && name.includes('blanket');
export const isBedPartMeshName = (name) => isMattressMeshName(name) || isBlanketMeshName(name);

// Cribs and bassinets are symmetric along X; beds have their head end at +X, so a
// fresh mattress is pulled toward the foot (−X) end of the frame.
export const HEAD_AT_PLUS_X_TYPES = ['ToddlerBed', 'TwinBed', 'QueenBed'];

// Every bed type can wear any of these blankets; each is the blanket of
// public/bed-parts/<id>.babylon, stored relative to that file's own mattress, so it is
// fitted to another bed by that mattress's proportions. A bed type's own entry (if
// any) is only its default; the Blanket* ones belong to no type (built by
// scripts/build-blanket-parts.mjs). A `separate` blanket hangs on the rail instead of
// draping over the mattress, so it is placed on its own.
export const BLANKET_STYLES = [
    { id: 'BlanketOpen', label: 'Open blanket' },
    { id: 'BlanketFolded', label: 'Folded blanket' },
    { id: 'QueenBed', label: 'Queen bed blanket' },
    { id: 'TwinBed', label: 'Twin bed blanket' },
    { id: 'ToddlerBed', label: 'Toddler bed blanket' },
    { id: 'Crib', label: 'Crib blanket (hangs on the rail)', separate: true },
];

export const isSeparateBlanket = (styleId) => Boolean(BLANKET_STYLES.find((s) => s.id === styleId)?.separate);
export const defaultBlanketStyle = (bedType) => BLANKET_STYLES.find((s) => s.id === bedType)?.id ?? null;

// The editable bedding's settings ride along in the saved .glb (glTF extras on the
// mattress mesh), so reopening the model brings the bedding back editable instead of
// as frozen meshes.
export const BEDDING_EXTRAS_KEY = 'dwfBedding';

/** Roles in public/bed-parts/<Type>.babylon (built by scripts/extract-bed-parts.mjs). */
const PART_ROLES = ['mattress', 'blanket', 'blanket_container'];

const templatesByScene = new WeakMap(); // scene -> Map(bedType -> Promise)

/**
 * Loads the bed parts file of a bed type once per scene as hidden template
 * meshes. Every type has a plain-white `mattress` (1 unit long along X, resting
 * on y=0, centered in XZ, catalog orientation: head end +X, long side facing −Z);
 * most also have a `blanket` and an invisible `blanket_container` plane, both
 * stored relative to the mattress TOP (y=0 = mattress top) so thickness and
 * blanket placement are independent. Resolves to
 * { templates: { role -> Mesh }, mattressSize: Vector3, hasBlanket, hasBlanketContainer }.
 */
const loadBedPartTemplates = (scene, bedType) => {
    let byType = templatesByScene.get(scene);
    if (!byType) {
        byType = new Map();
        templatesByScene.set(scene, byType);
    }
    let pending = byType.get(bedType);
    if (!pending) {
        pending = SceneLoader.ImportMeshAsync('', `${process.env.PUBLIC_URL}/bed-parts/`, `${bedType}.babylon`, scene).then(
            (result) => {
                const templates = {};
                result.meshes.forEach((mesh) => {
                    mesh.setEnabled(false);
                    mesh.isPickable = false;
                    if (PART_ROLES.includes(mesh.name)) templates[mesh.name] = mesh;
                });
                if (!templates.mattress) throw new Error(`bed-parts/${bedType}.babylon has no mattress mesh`);
                const b = worldBounds([templates.mattress]);
                return {
                    templates,
                    mattressSize: b.max.subtract(b.min),
                    hasBlanket: Boolean(templates.blanket),
                    hasBlanketContainer: Boolean(templates.blanket_container),
                };
            },
        );
        byType.set(bedType, pending);
        pending.catch(() => byType.delete(bedType));
    }
    return pending;
};

/**
 * Everything the editor needs to dress a bed of `bedType`: its own mattress plus
 * every blanket style. Resolves to
 * { mattress: Mesh, mattressSize: Vector3,
 *   blankets: { styleId -> { blanket: Mesh, container?: Mesh, mattressSize: Vector3 } } }.
 */
export const loadBedParts = async (scene, bedType) => {
    const [own, ...styles] = await Promise.all(
        [bedType, ...BLANKET_STYLES.map((s) => s.id)].map((type) => loadBedPartTemplates(scene, type)),
    );
    const blankets = {};
    BLANKET_STYLES.forEach(({ id }, i) => {
        const { templates, mattressSize } = styles[i];
        if (!templates.blanket) throw new Error(`bed-parts/${id}.babylon has no blanket mesh`);
        blankets[id] = { blanket: templates.blanket, container: templates.blanket_container, mattressSize };
    });
    return { mattress: own.templates.mattress, mattressSize: own.mattressSize, blankets };
};
